// Creative spec shape families come from the backend creative specs registry
// (services/creativePlatformSpecsRegistry.js — the single source of truth),
// generated into this committed artifact by
// `node scripts/generateCreativePlatformSpecs.js`. The backend owns the spec
// numbers; Creative Studio still owns ALL UI scoring/ranking/labeling and the
// approved-platform gating below. Unconfirmed / partially_confirmed specs in
// the artifact are NOT turned into distributable platform labels here.
import { isYahooPlatform, placeYahooAfterAmazon } from "./platformPresentationOrder";
import creativePlatformSpecs from "../data/creativePlatformSpecs.json";

const SPEC_PLATFORMS = creativePlatformSpecs.platforms || {};

// Adapt a serialized registry aspect family { key, ratio, minWidth, ... } into
// the { ratio, min:{width,height}, max:{width,height} } shape this module's
// dimension-fit check expects.
const specFamily = (family) => ({
  key: family.key,
  ratio: family.ratio,
  min: { width: family.minWidth, height: family.minHeight },
  max: { width: family.maxWidth, height: family.maxHeight },
});

const specFamiliesFor = (platformKey, formatKey, keyFilter = null) =>
  (SPEC_PLATFORMS?.[platformKey]?.formats?.[formatKey]?.aspectFamilies || [])
    .filter((family) => (keyFilter ? keyFilter.includes(family.key) : true))
    .map(specFamily);

const APPROVED_PLAN_STATUSES = new Set([
  "APPROVED",
  "COMPLETED",
  "EXECUTED",
  "COUNTERSIGNED",
]);

const PLATFORM_DEFINITIONS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
  { key: "x", label: "X" },
  { key: "youtube", label: "YouTube" },
  { key: "google_search", label: "Google Search" },
  { key: "rumble", label: "Rumble" },
  { key: "truth_social", label: "Truth Social" },
  { key: "reddit", label: "Reddit" },
  { key: "amazon", label: "Amazon" },
  // Viant is the platform name; its reporting channel (Display, CTV, ...) is
  // not part of it. Historical "Viant DSP (Display)" still resolves through
  // PLATFORM_ALIASES below.
  { key: "viant", label: "Viant" },
  { key: "shopify", label: "Shopify" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "ctv", label: "CTV" },
  { key: "video", label: "Video" },
  { key: "display", label: "Display" },
  { key: "mobile", label: "Mobile" },
  { key: "audio", label: "Audio" },
  { key: "ingame", label: "In-Game" },
  { key: "dooh", label: "DOOH" },
  { key: "cinema", label: "Cinema" },
  { key: "email", label: "Email" },
];

// Draft spec identities are recognized only inside the creative workflow.
// This does not alter analytics tiles or infer an approved campaign scope.
for (const key of ["yahoo", "viant"]) {
  if (SPEC_PLATFORMS[key] && !PLATFORM_DEFINITIONS.some(p => p.key === key)) {
    PLATFORM_DEFINITIONS.push({ key, label: SPEC_PLATFORMS[key].displayName });
  }
}
const PLATFORM_LABEL_BY_KEY = PLATFORM_DEFINITIONS.reduce((acc, platform) => {
  acc[platform.key] = platform.label;
  return acc;
}, {});

const PLATFORM_DISPLAY_PRIORITY = new Map(
  placeYahooAfterAmazon([...PLATFORM_DEFINITIONS.map((platform) => platform.key), "yahoo"])
    .map((key, index) => [key, index])
);

const PLATFORM_ALIASES = new Map([
  ["facebook", "facebook"],
  ["fb", "facebook"],
  ["meta", "facebook"],
  ["instagram", "instagram"],
  ["insta", "instagram"],
  ["ig", "instagram"],
  ["tiktok", "tiktok"],
  ["tik tok", "tiktok"],
  ["twitter", "x"],
  ["x", "x"],
  ["youtube", "youtube"],
  ["you tube", "youtube"],
  ["yt", "youtube"],
  ["google search", "google_search"],
  ["search", "google_search"],
  ["google_search", "google_search"],
  ["rumble", "rumble"],
  ["truth social", "truth_social"],
  ["truthsocial", "truth_social"],
  ["truth_social", "truth_social"],
  ["reddit", "reddit"],
  ["amazon", "amazon"],
  ["viant", "viant"],
  ["viant dsp", "viant"],
  ["viant_dsp", "viant"],
  ["viant dsp (display)", "viant"],
  ["yahoo", "yahoo"], ["yahoo dsp", "yahoo"], ["yahoodsp", "yahoo"], ["yahoo ads", "yahoo"],
  ["viant dsp display", "viant"], ["viantdsp", "viant"], ["adelphic", "viant"],
  ["shopify", "shopify"],
  ["shopify store", "shopify"],
  ["linkedin", "linkedin"],
  ["linked in", "linkedin"],
  ["linkedin ads", "linkedin"],
  ["ctv", "ctv"],
  ["connected tv", "ctv"],
  ["ott", "ctv"],
  ["video", "video"],
  ["display", "display"],
  ["banner", "display"],
  ["mobile", "mobile"],
  ["audio", "audio"],
  ["in game", "ingame"],
  ["ingame", "ingame"],
  ["dooh", "dooh"],
  ["out of home", "dooh"],
  ["cinema", "cinema"],
  ["theatrical", "cinema"],
  ["theater", "cinema"],
  ["theaters", "cinema"],
  ["theatre", "cinema"],
  ["theatres", "cinema"],
  ["email", "email"],
]);

// Display banner sizes are frontend-LOCAL for quick labeling, but they mirror
// the backend registry's confirmed display standardSizes. The parity tests
// guard that Studio's Format column and backend eligibility stay aligned.
export const DISPLAY_SIZES = new Set([
  "300x250",
  "336x280",
  "728x90",
  "970x90",
  "970x250",
  "320x50",
  "320x100",
  "300x50",
  "300x100",
  "300x600",
  "160x600",
  "300x1050",
  "468x60",
  "120x600",
  "200x200",
  "240x400",
  "250x250",
  "250x360",
  "580x400",
  "930x180",
  "980x120",
]);

// Recognized Display interstitial / full-screen sizes. These are Display
// creatives of a different format CLASS from standard banners, mirrored
// from the backend registry's crossClassRecognizedSizes.
export const DISPLAY_INTERSTITIAL_SIZES = new Set([
  "1024x768",
  "768x1024",
  "320x480",
  "480x320",
]);

const VIDEO_COMPATIBILITY_PROFILES = {
  widescreen: [
    { key: "ctv", score: 82 },
    { key: "youtube", score: 80 },
    { key: "video", score: 74 },
    { key: "cinema", score: 62 },
    { key: "dooh", score: 58 },
  ],
  vertical: [
    { key: "tiktok", score: 88 },
    { key: "instagram", score: 82 },
    { key: "facebook", score: 68 },
    { key: "youtube", score: 56 },
    { key: "mobile", score: 48 },
    { key: "video", score: 44 },
  ],
  portrait: [
    { key: "instagram", score: 78 },
    { key: "facebook", score: 70 },
    { key: "youtube", score: 60 },
    { key: "tiktok", score: 62 },
    { key: "mobile", score: 46 },
    { key: "video", score: 42 },
  ],
  square: [
    { key: "facebook", score: 72 },
    { key: "instagram", score: 70 },
    { key: "youtube", score: 54 },
    { key: "tiktok", score: 56 },
    { key: "mobile", score: 42 },
    { key: "video", score: 40 },
  ],
  unknown: [{ key: "video", score: 64 }],
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeUpper = (value) => String(value || "").trim().toUpperCase();

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, "")
    .replace(/\s+/g, " ")
    .trim();

const humanizeValue = (value, fallback = "—") => {
  const normalized = String(value || "").trim();
  if (!normalized) return fallback;
  return normalized
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const pickFiniteNumber = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return null;
};

const extractPlatformTokens = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractPlatformTokens(entry));
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
  }

  if (value && typeof value === "object") {
    const nested = value.platforms || value.targetPlatforms || value.target_platforms || value.selected || [];
    const direct = [
      value.key,
      value.value,
      value.label,
      value.name,
      value.platform,
      value.platformKey,
      value.platform_key,
      value.channel,
      value.type,
    ]
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);

    return [...direct, ...extractPlatformTokens(nested)];
  }

  return [];
};

export const normalizePlatformArray = (value) =>
  Array.from(
    new Set(
      extractPlatformTokens(value)
        .map((entry) => normalizePlatformKey(entry))
        .filter(Boolean)
    )
  );

const expandApprovedPlanPlatformToken = (value) => {
  const normalized = normalizeKey(value);
  if (!normalized) return [];

  if (
    normalized === "meta" ||
    normalized === "meta social" ||
    normalized === "meta socials" ||
    normalized === "meta platforms" ||
    normalized === "meta platform" ||
    normalized === "facebook instagram" ||
    normalized === "instagram facebook" ||
    normalized === "facebook and instagram" ||
    normalized === "instagram and facebook"
  ) {
    return ["facebook", "instagram"];
  }

  if (normalized === "meta facebook" || normalized === "facebook meta") {
    return ["facebook"];
  }

  if (normalized === "meta instagram" || normalized === "instagram meta") {
    return ["instagram"];
  }

  const canonical = normalizePlatformKey(value);
  return canonical ? [canonical] : [];
};

const normalizeApprovedPlanPlatformArray = (value) =>
  Array.from(
    new Set(
      extractPlatformTokens(value)
        .flatMap((entry) => expandApprovedPlanPlatformToken(entry))
        .filter(Boolean)
    )
  );

const extractBudgetBreakdownPlatformTokens = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractBudgetBreakdownPlatformTokens(entry));
  }

  if (typeof value !== "object") return [];

  return Object.keys(value)
    .map((key) => String(key || "").trim())
    .filter(Boolean);
};

/*
 * The contract vocabulary of `selected_platforms_hard_allowlist_v1`, inverted.
 *
 * The allowlist speaks in the policy's own uppercase tokens, which are not the
 * app's platform keys — DISPLAY_RETARGETING is one channel named two ways, and
 * reading it with the generic key normalizer silently drops Display from a
 * campaign that bought it. Mirrors PLATFORM_SCOPE_CONTRACT_KEYS in the
 * backend's sherlocPlatformScopeService, which issues these tokens.
 */
const PLATFORM_SCOPE_CONTRACT_KEY_TO_PLATFORM = Object.freeze({
  META: "facebook",
  YOUTUBE: "youtube",
  CTV: "ctv",
  TIKTOK: "tiktok",
  DISPLAY_RETARGETING: "display",
  X: "x",
  GOOGLE_SEARCH: "google_search",
  CHATGPT_SEARCH: "chatgpt_search",
  RUMBLE: "rumble",
  TRUTH_SOCIAL: "truth_social",
  REDDIT: "reddit",
  AMAZON: "amazon",
  YAHOO_DSP: "yahoo",
  VIANT: "viant",
  SHOPIFY: "shopify",
  LINKEDIN: "linkedin",
  VIDEO: "video",
  MOBILE: "mobile",
  AUDIO: "audio",
  IN_GAME: "ingame",
  DOOH: "dooh",
  EMAIL: "email",
  CINEMA: "cinema",
});

/*
 * The governed platform allowlist a campaign carries, if it carries one.
 *
 * `selected_platforms_hard_allowlist_v1` is the canonical media-platform scope
 * contract — the same policy the backend's sherlocPlatformScopeService issues
 * and enforces. When a campaign declares it, that list IS the campaign's media
 * platforms and nothing may widen it: not a budget line, not a channel alias,
 * and above all not a word recovered from prose.
 */
const extractGovernedPlatformAllowlist = (campaign) => {
  const contract =
    campaign?.strategy_payload?.platformScopeContract ||
    campaign?.strategyPayload?.platformScopeContract ||
    campaign?.platformScopeContract ||
    null;
  if (!contract || typeof contract !== "object") return null;
  const allowed = Array.isArray(contract.allowedPlatforms)
    ? contract.allowedPlatforms
    : Array.isArray(contract.allowed_platforms)
    ? contract.allowed_platforms
    : null;
  if (!allowed) return null;
  const keys = allowed
    .map((entry) => {
      const token = String(entry || "").trim().toUpperCase();
      return PLATFORM_SCOPE_CONTRACT_KEY_TO_PLATFORM[token] || normalizePlatformKey(entry);
    })
    .filter(Boolean);
  return keys.length ? new Set(keys) : null;
};

/*
 * Whether the campaign states its media platforms STRUCTURALLY — a platform
 * array, a channel array, or a budget allocation. This is the precondition the
 * free-text recovery below always claimed and never checked.
 */
const hasStructuredApprovedPlanPlatforms = (campaign) =>
  normalizeApprovedPlanPlatformArray([
    campaign?.target_platforms,
    campaign?.targetPlatforms,
    campaign?.platforms,
    campaign?.channels,
    campaign?.channel_mix,
    campaign?.channelMix,
    campaign?.media_mix,
    campaign?.mediaMix,
    campaign?.recommended_channels,
    campaign?.recommendedChannels,
    campaign?.strategy_payload?.platforms,
    campaign?.strategyPayload?.platforms,
    campaign?.strategy_payload?.channels,
    campaign?.strategyPayload?.channels,
    campaign?.strategy_payload?.channel_mix,
    campaign?.strategyPayload?.channelMix,
    campaign?.strategy_payload?.media_mix,
    campaign?.strategyPayload?.mediaMix,
    campaign?.strategy_payload?.recommended_channels,
    campaign?.strategyPayload?.recommendedChannels,
    extractBudgetBreakdownPlatformTokens(campaign?.budget_breakdown),
    extractBudgetBreakdownPlatformTokens(campaign?.budgetBreakdown),
    extractBudgetBreakdownPlatformTokens(campaign?.strategy_payload?.budget_breakdown),
    extractBudgetBreakdownPlatformTokens(campaign?.strategy_payload?.budgetBreakdown),
    extractBudgetBreakdownPlatformTokens(campaign?.strategyPayload?.budget_breakdown),
    extractBudgetBreakdownPlatformTokens(campaign?.strategyPayload?.budgetBreakdown),
  ]).length > 0;

/*
 * A LAST resort, and now actually last.
 *
 * Its own precondition — "some approved campaigns encode theatrical intent ONLY
 * in their approved name/notes rather than structured platform arrays" — was
 * never enforced, so it ran against every campaign including ones that state
 * their platforms precisely. Never Stop Chasing Incremental Markets (campaign
 * 50) buys YouTube and CTV, and its planner notes read "Split evenly across
 * these theater ZIPs: 41071, 45069, ...". That is a GEO instruction for the
 * YouTube/CTV buy. The bare word "theater" turned it into a cinema media
 * platform the campaign has no allocation, no budget line and no allowlist
 * entry for.
 *
 * The canonical backend scope service takes the same position and goes
 * further: it has no cinema text pattern at all, and requires paid-media
 * qualifiers even for words like "display". Prose is evidence of intent only
 * where there is no structured statement of it.
 */
const extractApprovedPlanContextFallbackKeys = (campaign) => {
  if (hasStructuredApprovedPlanPlatforms(campaign)) return [];

  const textBlob = [
    campaign?.campaign_name,
    campaign?.campaignName,
    campaign?.objective,
    campaign?.notes,
    campaign?.strategy_payload?.notes,
    campaign?.strategyPayload?.notes,
  ]
    .map((entry) => normalizeKey(entry))
    .filter(Boolean)
    .join(" ");

  if (!textBlob) return [];

  // Some approved campaigns encode theatrical intent only in their approved name/notes
  // rather than structured platform arrays. Recover cinema conservatively from that text.
  if (
    textBlob.includes("cinema") ||
    textBlob.includes("theatrical") ||
    textBlob.includes("theater") ||
    textBlob.includes("theatre") ||
    textBlob.includes("movie theater") ||
    textBlob.includes("movie theatre")
  ) {
    return ["cinema"];
  }

  return [];
};

export const normalizePlatformKey = (value) => {
  if (!value && value !== 0) return "";

  const normalized = normalizeKey(value);
  if (!normalized) return "";

  if (PLATFORM_ALIASES.has(normalized)) return PLATFORM_ALIASES.get(normalized) || "";

  if (normalized === "googlesearch" || normalized === "paid search") return "google_search";
  if (normalized === "truthsocial") return "truth_social";
  if (normalized === "connectedtv") return "ctv";
  if (normalized === "outofhome" || normalized === "digital out of home") return "dooh";
  if (normalized === "ingame") return "ingame";
  if (normalized === "movie theater" || normalized === "movie theatre") return "cinema";

  return PLATFORM_LABEL_BY_KEY[normalized] ? normalized : "";
};

export const platformLabelFromKey = (value) => {
  const key = normalizePlatformKey(value);
  return key ? PLATFORM_LABEL_BY_KEY[key] : "";
};

export const compareCreativePlatformKeys = (left, right) => {
  const normalizedLeft = isYahooPlatform(left) ? "yahoo" : normalizePlatformKey(left);
  const normalizedRight = isYahooPlatform(right) ? "yahoo" : normalizePlatformKey(right);
  const leftPriority = PLATFORM_DISPLAY_PRIORITY.get(normalizedLeft);
  const rightPriority = PLATFORM_DISPLAY_PRIORITY.get(normalizedRight);

  if (leftPriority != null && rightPriority != null && leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }
  if (leftPriority != null && rightPriority == null) return -1;
  if (leftPriority == null && rightPriority != null) return 1;

  return String(platformLabelFromKey(normalizedLeft) || normalizedLeft || "")
    .localeCompare(String(platformLabelFromKey(normalizedRight) || normalizedRight || ""));
};

// A multi-platform creative occupies ONE position in a platform-ordered list:
// that of its highest-priority platform per PLATFORM_DEFINITIONS. It is never
// duplicated once per platform for sorting.
export const primaryCreativePlatformKey = (platformKeys = []) => {
  const keys = (Array.isArray(platformKeys) ? platformKeys : [])
    .map((key) => String(key || "").trim())
    .filter(Boolean);
  if (!keys.length) return "";
  return keys.slice().sort(compareCreativePlatformKeys)[0];
};

// Row-level canonical platform ordering for creative libraries. Both Creative
// Studio and Creative Assets (Cortex and Quantum alike) must answer "what
// order do Cphere platforms appear in?" from this one comparator so the two
// surfaces can never drift. Platform position comes first; recency orders
// rows only WITHIN a platform group; the asset id keeps full determinism when
// even recency ties. Rows with no resolvable platform sort last, after
// unknown platforms — never dropped.
export const compareCreativeLibraryRowsByPlatform = (left, right) => {
  const leftPrimary = primaryCreativePlatformKey(left?.platformKeys);
  const rightPrimary = primaryCreativePlatformKey(right?.platformKeys);
  if (leftPrimary && !rightPrimary) return -1;
  if (!leftPrimary && rightPrimary) return 1;
  const platformOrder = compareCreativePlatformKeys(leftPrimary, rightPrimary);
  if (platformOrder !== 0) return platformOrder;

  const leftTime = new Date(left?.lastUpdated || 0).getTime() || 0;
  const rightTime = new Date(right?.lastUpdated || 0).getTime() || 0;
  if (rightTime !== leftTime) return rightTime - leftTime;

  return String(left?.assetId ?? "").localeCompare(String(right?.assetId ?? ""), undefined, {
    numeric: true,
  });
};

export const isApprovedPlanCampaign = (campaign) => {
  if (!campaign || typeof campaign !== "object") return false;

  if (campaign.countersigned_at || campaign.countersignedAt) return true;

  return [
    campaign.contract_status,
    campaign.contractStatus,
    campaign.authorization_status,
    campaign.authorizationStatus,
    campaign.status,
  ].some((status) => APPROVED_PLAN_STATUSES.has(normalizeUpper(status)));
};

const parseDateMs = (value, boundary = "end") => {
  if (!value) return 0;
  const raw = String(value).trim();
  if (!raw) return 0;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const suffix = boundary === "start" ? "T00:00:00.000Z" : "T23:59:59.999Z";
    const parsed = Date.parse(`${raw}${suffix}`);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const selectApprovedPlanCampaign = ({ campaigns, nowMs = Date.now() } = {}) => {
  const approved = (Array.isArray(campaigns) ? campaigns : []).filter(isApprovedPlanCampaign);
  if (!approved.length) return null;
  if (approved.length === 1) return approved[0];

  const ranked = approved
    .map((campaign) => {
      const startMs = parseDateMs(
        campaign.flight_start_date || campaign.flightStart || campaign.flight_start,
        "start"
      );
      const endMs = parseDateMs(
        campaign.flight_end_date || campaign.flightEnd || campaign.flight_end,
        "end"
      );
      const updatedMs = parseDateMs(
        campaign.updated_at || campaign.updatedAt || campaign.created_at || campaign.createdAt,
        "end"
      );

      let score = 0;
      if (campaign.countersigned_at || campaign.countersignedAt) score += 200;
      if (startMs && endMs && nowMs >= startMs && nowMs <= endMs) score += 120;
      else if (startMs && nowMs < startMs) score += 60;
      else if (endMs && nowMs > endMs) score += 30;
      if (Array.isArray(campaign.platforms) && campaign.platforms.length) score += 20;
      if (startMs && endMs) score += 10;

      return { campaign, score, updatedMs };
    })
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return right.updatedMs - left.updatedMs;
    });

  return ranked[0]?.campaign || approved[0];
};

export const buildApprovedPlanContext = (campaigns) => {
  const approvedCampaign = selectApprovedPlanCampaign({ campaigns });
  const approvedCampaigns = (Array.isArray(campaigns) ? campaigns : []).filter(isApprovedPlanCampaign);
  const approvedPlatformKeys = normalizeApprovedPlanPlatformArray([
    approvedCampaign?.target_platforms,
    approvedCampaign?.targetPlatforms,
    approvedCampaign?.platforms,
    approvedCampaign?.channels,
    approvedCampaign?.channel_mix,
    approvedCampaign?.channelMix,
    approvedCampaign?.media_mix,
    approvedCampaign?.mediaMix,
    approvedCampaign?.recommended_channels,
    approvedCampaign?.recommendedChannels,
    approvedCampaign?.strategy_payload?.platforms,
    approvedCampaign?.strategyPayload?.platforms,
    approvedCampaign?.strategy_payload?.channels,
    approvedCampaign?.strategyPayload?.channels,
    approvedCampaign?.strategy_payload?.channel_mix,
    approvedCampaign?.strategyPayload?.channelMix,
    approvedCampaign?.strategy_payload?.media_mix,
    approvedCampaign?.strategyPayload?.mediaMix,
    approvedCampaign?.strategy_payload?.recommended_channels,
    approvedCampaign?.strategyPayload?.recommendedChannels,
    extractBudgetBreakdownPlatformTokens(approvedCampaign?.budget_breakdown),
    extractBudgetBreakdownPlatformTokens(approvedCampaign?.budgetBreakdown),
    extractBudgetBreakdownPlatformTokens(approvedCampaign?.strategy_payload?.budget_breakdown),
    extractBudgetBreakdownPlatformTokens(approvedCampaign?.strategy_payload?.budgetBreakdown),
    extractBudgetBreakdownPlatformTokens(approvedCampaign?.strategyPayload?.budget_breakdown),
    extractBudgetBreakdownPlatformTokens(approvedCampaign?.strategyPayload?.budgetBreakdown),
    extractApprovedPlanContextFallbackKeys(approvedCampaign),
  ]);

  /*
   * The governed allowlist is a ceiling, not another input. Anything the
   * derivation above recovered that the campaign's own hard allowlist excludes
   * is not this campaign's platform.
   */
  const governedAllowlist = extractGovernedPlatformAllowlist(approvedCampaign);
  const scopedPlatformKeys = governedAllowlist
    ? approvedPlatformKeys.filter((key) => governedAllowlist.has(key))
    : approvedPlatformKeys;

  return {
    approvedCampaign,
    approvedCampaigns,
    approvedPlatformKeys: scopedPlatformKeys,
    approvedPlatformLabels: scopedPlatformKeys
      .map((key) => platformLabelFromKey(key))
      .filter(Boolean),
  };
};

const extractDimensionPair = (value) => {
  const source = String(value || "");
  if (!source) return null;

  const match = source.match(/(^|[^0-9])(\d{2,4})\s*[x×]\s*(\d{2,4})(?!\d)/i);
  if (!match) return null;

  const width = Number(match[2]);
  const height = Number(match[3]);
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (width <= 0 || height <= 0) return null;
  return { width, height };
};

const detectDimensions = (asset) => {
  const metadata = asset?.metadata || {};
  const versionMetadata = asset?.currentVersion?.metadata || asset?.approvedVersion?.metadata || {};
  // Persisted-at-upload intrinsic metadata (backend generation_meta.metadata).
  const studioMeta = asset?.generation_meta?.metadata || {};
  const width = pickFiniteNumber(
    asset?.width,
    asset?.asset_width,
    asset?.dimensions?.width,
    studioMeta?.width,
    metadata?.width,
    metadata?.videoWidth,
    metadata?.imageWidth,
    metadata?.naturalWidth,
    metadata?.pixelWidth,
    versionMetadata?.width,
    versionMetadata?.videoWidth,
    versionMetadata?.imageWidth,
    asset?.currentVersion?.width,
    asset?.approvedVersion?.width
  );
  const height = pickFiniteNumber(
    asset?.height,
    asset?.asset_height,
    asset?.dimensions?.height,
    studioMeta?.height,
    metadata?.height,
    metadata?.videoHeight,
    metadata?.imageHeight,
    metadata?.naturalHeight,
    metadata?.pixelHeight,
    versionMetadata?.height,
    versionMetadata?.videoHeight,
    versionMetadata?.imageHeight,
    asset?.currentVersion?.height,
    asset?.approvedVersion?.height
  );

  if (width && height) {
    return {
      width: Math.round(width),
      height: Math.round(height),
    };
  }

  return (
    extractDimensionPair(asset?.file_name) ||
    extractDimensionPair(asset?.currentVersion?.file_name) ||
    extractDimensionPair(asset?.title) ||
    extractDimensionPair(asset?.format) ||
    extractDimensionPair(metadata?.resolution) ||
    extractDimensionPair(metadata?.dimensions) ||
    extractDimensionPair(versionMetadata?.resolution) ||
    extractDimensionPair(versionMetadata?.dimensions) ||
    null
  );
};

const normalizeRatioToken = (value) => {
  const raw = String(value || "")
    .replace(/\s+/g, "")
    .replace(/×/g, "x")
    .toLowerCase();

  if (raw === "16x9" || raw === "16:9") return "16:9";
  if (raw === "9x16" || raw === "9:16") return "9:16";
  if (raw === "4x5" || raw === "4:5") return "4:5";
  if (raw === "1x1" || raw === "1:1") return "1:1";
  return "";
};

const extractRatioToken = (value) => {
  const source = String(value || "");
  if (!source) return "";

  const match = source.match(/(^|[^0-9])((?:1|4|9|16)\s*(?:x|×|:)\s*(?:1|4|5|9|16))(?!\d)/i);
  if (!match) return "";

  return normalizeRatioToken(match[2]);
};

const detectRatioToken = (asset) => {
  return (
    extractRatioToken(asset?.file_name) ||
    extractRatioToken(asset?.currentVersion?.file_name) ||
    extractRatioToken(asset?.title) ||
    extractRatioToken(asset?.notes) ||
    extractRatioToken(asset?.format) ||
    extractRatioToken(asset?.metadata?.format) ||
    extractRatioToken(asset?.generation_meta?.format) ||
    extractRatioToken(asset?.metadata?.dimensions) ||
    extractRatioToken(asset?.metadata?.resolution) ||
    extractRatioToken(asset?.currentVersion?.metadata?.dimensions) ||
    extractRatioToken(asset?.currentVersion?.metadata?.resolution) ||
    extractRatioToken(asset?.approvedVersion?.metadata?.dimensions) ||
    extractRatioToken(asset?.approvedVersion?.metadata?.resolution) ||
    normalizeRatioToken(
      asset?.aspect_ratio ||
        asset?.aspectRatio ||
        asset?.metadata?.aspect_ratio ||
        asset?.metadata?.aspectRatio ||
        asset?.currentVersion?.aspect_ratio ||
        asset?.currentVersion?.aspectRatio ||
        asset?.approvedVersion?.aspect_ratio ||
        asset?.approvedVersion?.aspectRatio
    ) ||
    ""
  );
};

const isVideoAsset = (asset) => {
  const mime = normalizeText(asset?.mime_type || asset?.currentVersion?.mime_type);
  const fileName = normalizeText(asset?.file_name || asset?.currentVersion?.file_name);
  if (mime.startsWith("video/")) return true;
  if (mime.startsWith("image/")) return false;
  return [".mp4", ".mov", ".webm", ".m4v", ".avi"].some((ext) => fileName.endsWith(ext));
};

const isImageAsset = (asset) => {
  const mime = normalizeText(asset?.mime_type || asset?.currentVersion?.mime_type);
  if (mime.startsWith("image/")) return true;
  return !isVideoAsset(asset);
};

const looksLikeLogoAsset = (asset) => {
  const haystack = [
    asset?.title,
    asset?.file_name,
    asset?.currentVersion?.file_name,
    asset?.notes,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return /\blogo\b|\bbrand asset\b|\bbrand\b/.test(haystack);
};

const ratioLabelFromDimensions = (dimensions) => {
  if (!dimensions?.width || !dimensions?.height) return "";
  const ratio = dimensions.width / dimensions.height;

  if (Math.abs(ratio - 1) < 0.08) return "1:1";
  if (Math.abs(ratio - 4 / 5) < 0.08) return "4:5";
  if (Math.abs(ratio - 9 / 16) < 0.08) return "9:16";
  if (Math.abs(ratio - 16 / 9) < 0.1) return "16:9";
  return "";
};

const sizeToken = (dimensions) =>
  dimensions?.width && dimensions?.height ? `${dimensions.width}x${dimensions.height}` : "";

const detectFormatLabel = ({ asset, dimensions, ratioLabel, isVideo, isLogo }) => {
  const existingFormat = String(asset?.format || "").trim();
  if (existingFormat) return humanizeValue(existingFormat);

  const dimensionToken = sizeToken(dimensions);
  if (isLogo) return "Brand Asset";
  if (dimensionToken && DISPLAY_INTERSTITIAL_SIZES.has(dimensionToken) && !isVideo) return `${dimensionToken} Interstitial`;
  if (dimensionToken && DISPLAY_SIZES.has(dimensionToken) && !isVideo) return `${dimensionToken} Banner`;

  if (isVideo) {
    if (ratioLabel === "9:16") return "9:16 Vertical Video";
    if (ratioLabel === "4:5") return "4:5 Vertical Video";
    if (ratioLabel === "1:1") return "1:1 Square Video";
    if (ratioLabel === "16:9") return "16:9 Video";
    return "Video";
  }

  if (dimensionToken && DISPLAY_INTERSTITIAL_SIZES.has(dimensionToken)) return `${dimensionToken} Interstitial`;
  if (dimensionToken && DISPLAY_SIZES.has(dimensionToken)) return `${dimensionToken} Banner`;
  if (ratioLabel === "9:16") return "9:16 Vertical Static";
  if (ratioLabel === "4:5") return "4:5 Portrait Static";
  if (ratioLabel === "1:1") return "1:1 Square Static";
  if (ratioLabel === "16:9") return "16:9 Static";
  if (isImageAsset(asset)) return "Static Image";
  return "—";
};

const addCandidate = (map, key, score, reason) => {
  const normalizedKey = normalizePlatformKey(key);
  if (!normalizedKey) return;

  const current = map.get(normalizedKey) || { score: 0, reasons: [] };
  current.score += score;
  if (reason) current.reasons.push(reason);
  map.set(normalizedKey, current);
};

const addCandidatesForTextSignals = ({ text, candidates }) => {
  const haystack = normalizeText(text).replace(/[_-]+/g, " ");
  if (!haystack) return;

  if (/\bfacebook\b|\bmeta\b|\bfb\b/.test(haystack)) addCandidate(candidates, "facebook", 85, "name");
  if (/\binstagram\b|\binsta\b|\big\b|\breel\b|\breels\b|\bstory\b|\bstories\b/.test(haystack)) {
    addCandidate(candidates, "instagram", 85, "name");
  }
  if (/\btik ?tok\b|\btiktok\b/.test(haystack)) addCandidate(candidates, "tiktok", 90, "name");
  if (/\btwitter\b|\bx\b/.test(haystack)) addCandidate(candidates, "x", 60, "name");
  if (/\byoutube\b|\byt\b|\bpre[- ]?roll\b|\bbumper\b/.test(haystack)) {
    addCandidate(candidates, "youtube", 90, "name");
  }
  if (/\bgoogle search\b|\bpaid search\b|\bsem\b|\bsearch\b/.test(haystack)) {
    addCandidate(candidates, "google_search", 88, "name");
  }
  if (/\brumble\b/.test(haystack)) addCandidate(candidates, "rumble", 90, "name");
  if (/\btruth social\b/.test(haystack)) addCandidate(candidates, "truth_social", 90, "name");
  if (/\breddit\b/.test(haystack)) addCandidate(candidates, "reddit", 90, "name");
  if (/\bamazon\b/.test(haystack)) addCandidate(candidates, "amazon", 90, "name");
  if (/\bctv\b|\bconnected tv\b|\bott\b|\b15tv\b|\b30tv\b|\b_tv\b/.test(haystack)) {
    addCandidate(candidates, "ctv", 88, "name");
  }
  if (/\bdooh\b|\booh\b|\bout of home\b/.test(haystack)) addCandidate(candidates, "dooh", 92, "name");
  if (/\bcinema\b|\btheater\b|\btheatre\b/.test(haystack)) addCandidate(candidates, "cinema", 92, "name");
  if (/\bdisplay\b|\bbanner\b|\bleaderboard\b|\bskyscraper\b|\bmrec\b/.test(haystack)) {
    addCandidate(candidates, "display", 86, "name");
  }
  if (/\bmobile\b|\binterstitial\b|\bin-app\b/.test(haystack)) addCandidate(candidates, "mobile", 74, "name");
  if (/\baudio\b|\bpodcast\b/.test(haystack)) addCandidate(candidates, "audio", 78, "name");
  if (/\bingame\b|\bin-game\b|\bgaming\b/.test(haystack)) addCandidate(candidates, "ingame", 78, "name");
  if (/\bemail\b|\bnewsletter\b/.test(haystack)) addCandidate(candidates, "email", 78, "name");
  if (/\bvideo\b/.test(haystack)) addCandidate(candidates, "video", 48, "name");
};

const getVideoCompatibilityProfile = ({ ratioLabel, textSignals }) => {
  if (ratioLabel === "16:9") return "widescreen";
  if (ratioLabel === "9:16") return "vertical";
  if (ratioLabel === "4:5") return "portrait";
  if (ratioLabel === "1:1") return "square";
  if (/\b(15tv|30tv|ctv|connected tv|ott|pre ?roll|preroll|bumper|trailer)\b/.test(textSignals)) {
    return "widescreen";
  }
  return "unknown";
};

const addApprovedVideoCompatibilityCandidates = ({
  candidates,
  approvedPlatformKeys,
  ratioLabel,
  textSignals,
}) => {
  const approvedSet = new Set(normalizeApprovedPlanPlatformArray(approvedPlatformKeys));
  if (!approvedSet.size) return;

  const profile = getVideoCompatibilityProfile({ ratioLabel, textSignals });
  const profileRules = VIDEO_COMPATIBILITY_PROFILES[profile] || VIDEO_COMPATIBILITY_PROFILES.unknown;

  profileRules.forEach(({ key, score }) => {
    if (approvedSet.has(key)) addCandidate(candidates, key, score, `video_profile_${profile}`);
  });

  if (profile === "widescreen" && /\b(cinema|theater|theatre)\b/.test(textSignals) && approvedSet.has("cinema")) {
    addCandidate(candidates, "cinema", 20, "video_cinema_intent");
  }
  if (profile === "widescreen" && /\b(dooh|ooh|out of home)\b/.test(textSignals) && approvedSet.has("dooh")) {
    addCandidate(candidates, "dooh", 20, "video_dooh_intent");
  }
  if (
    (profile === "vertical" || profile === "portrait" || profile === "square") &&
    /\b(reel|reels|story|stories|social|short|shorts)\b/.test(textSignals)
  ) {
    ["instagram", "facebook", "tiktok", "youtube"].forEach((key) => {
      if (approvedSet.has(key)) addCandidate(candidates, key, 14, "video_social_intent");
    });
  }
};

// LinkedIn creative dimension families (official LinkedIn ad-spec categories:
// single-image landscape ~1.91:1 / square 1:1; video 16:9 / 1:1 / 4:5 / 9:16).
// Ratio match within 5%; dimensions must sit inside the family box. Sourced
// from the backend registry artifact (linkedin.single_image / linkedin.video).
const LINKEDIN_IMAGE_FAMILIES = specFamiliesFor("linkedin", "single_image");
const LINKEDIN_VIDEO_FAMILIES = specFamiliesFor("linkedin", "video");
const LINKEDIN_RATIO_TOLERANCE = 0.05;

// SHAPE match, mirroring the backend registry's `shapeFits`. A family's minimum
// is a real floor; its maximum is the largest rendition the platform SERVES,
// not a limit on the master an advertiser supplies. Gating on the maximum here
// would re-create, in this suggestion layer, the same defect that made 4K
// masters look like they matched no platform at all.
const fitsDimensionFamily = (dimensions, families, tolerance = LINKEDIN_RATIO_TOLERANCE) => {
  if (!dimensions?.width || !dimensions?.height) return false;
  const actual = dimensions.width / dimensions.height;
  return families.some(
    (family) =>
      Math.abs(actual - family.ratio) <= family.ratio * tolerance &&
      dimensions.width >= family.min.width && dimensions.height >= family.min.height
  );
};

// Meta (Facebook/Instagram) LANDSCAPE feed/link image family — 1.91:1 and
// 16:9. The existing ratio-token block covers square (1:1), portrait (4:5),
// and vertical (9:16) image/social candidates, but ratioLabelFromDimensions
// emits no token for ~1.91 (and no image candidate for 16:9), so landscape
// link/feed images had no Meta candidate. This is the one confirmed spec gap;
// it is image-only, approved-gated, and never overlaps DISPLAY_SIZES (so it
// cannot outrank Display for standard banners). Vertical/portrait/square and
// all video families keep their existing handling.
// Sourced from the backend registry artifact (meta.feed_image), filtered to the
// two LANDSCAPE families — square/portrait/vertical are handled by the
// ratio-token social block above.
const META_LANDSCAPE_IMAGE_FAMILIES = specFamiliesFor("meta", "feed_image", [
  "landscape_1_91_1",
  "landscape_16_9",
]);

const addApprovedLandscapeImageMetaCandidates = ({ candidates, approvedPlatformKeys, dimensions, isVideo, isImage }) => {
  if (isVideo || !isImage) return; // landscape link/feed IMAGE family only
  if (!fitsDimensionFamily(dimensions, META_LANDSCAPE_IMAGE_FAMILIES)) return;
  const approvedSet = new Set(normalizeApprovedPlanPlatformArray(approvedPlatformKeys));
  // Pool filter also enforces approval downstream; gate here too so the
  // candidate is never generated for an unselected platform.
  if (approvedSet.has("facebook")) addCandidate(candidates, "facebook", 64, "meta_landscape_image_family");
  if (approvedSet.has("instagram")) addCandidate(candidates, "instagram", 60, "meta_landscape_image_family");
  if (approvedSet.has("meta")) addCandidate(candidates, "meta", 64, "meta_landscape_image_family");
};

// Approved-gated LinkedIn candidate: LinkedIn surfaces in the Platform column
// ONLY when it is approved/selected for the campaign AND the asset is
// dimensionally compatible with a LinkedIn ad family. Missing dimensions add
// no candidate (conservative — unknown metadata never yields a false eligible
// label). The existing pool filter already drops non-approved candidates, so
// this never makes LinkedIn distributable when it is not selected.
const addApprovedLinkedInCompatibilityCandidates = ({ candidates, approvedPlatformKeys, dimensions, isVideo, isImage }) => {
  const approvedSet = new Set(normalizeApprovedPlanPlatformArray(approvedPlatformKeys));
  if (!approvedSet.has("linkedin")) return;
  if (isVideo && fitsDimensionFamily(dimensions, LINKEDIN_VIDEO_FAMILIES)) {
    addCandidate(candidates, "linkedin", 72, "linkedin_video_family");
  } else if (isImage && fitsDimensionFamily(dimensions, LINKEDIN_IMAGE_FAMILIES)) {
    addCandidate(candidates, "linkedin", 72, "linkedin_image_family");
  }
};

const getGuaranteedVideoCompatibilityKeys = ({ isVideo, ratioLabel }) => {
  if (!isVideo) return [];
  if (ratioLabel === "16:9" || ratioLabel === "9:16" || ratioLabel === "1:1") {
    return ["youtube"];
  }
  return [];
};

export const resolveCreativeStudioAssetMeta = (asset, options = {}) => {
  const approvedPlatformKeys = normalizeApprovedPlanPlatformArray(options.approvedPlatformKeys);
  const approvedPlatformSet = new Set(approvedPlatformKeys);
  const dimensions = detectDimensions(asset);
  const ratioLabel = ratioLabelFromDimensions(dimensions) || detectRatioToken(asset);
  const isVideo = isVideoAsset(asset);
  const isLogo = looksLikeLogoAsset(asset);
  const candidates = new Map();

  const explicitPlatformKeys = normalizePlatformArray([
    asset?.channel,
    asset?.platform,
    asset?.platformKey,
    asset?.platform_key,
    asset?.platforms,
    asset?.targetPlatforms,
    asset?.target_platforms,
    asset?.mappings,
    asset?.generation_meta?.channel,
    asset?.generation_meta?.platform,
    asset?.generation_meta?.platformKey,
    asset?.generation_meta?.platform_key,
    asset?.generation_meta?.platforms,
    asset?.generation_meta?.platformHints,
    asset?.metadata?.channel,
    asset?.metadata?.platform,
    asset?.metadata?.platformKey,
    asset?.metadata?.platform_key,
    asset?.metadata?.platforms,
    asset?.currentVersion?.platform,
    asset?.currentVersion?.platformKey,
    asset?.currentVersion?.platform_key,
    asset?.currentVersion?.platforms,
  ]);
  explicitPlatformKeys.forEach((platformKey, index) => {
    addCandidate(candidates, platformKey, index === 0 ? 120 : 112, "existing_platform");
  });

  const rawPlatform =
    String(asset?.channel || "").trim() ||
    explicitPlatformKeys[0] ||
    String(asset?.generation_meta?.channel || asset?.generation_meta?.platform || "").trim() ||
    String(asset?.metadata?.channel || asset?.metadata?.platform || "").trim();

  const rawFormat = normalizeText(asset?.format || asset?.generation_meta?.format || asset?.metadata?.format);
  if (rawFormat.includes("banner")) addCandidate(candidates, "display", 76, "existing_format");
  if (rawFormat.includes("video")) addCandidate(candidates, "video", 68, "existing_format");
  if (rawFormat.includes("ctv")) addCandidate(candidates, "ctv", 84, "existing_format");
  if (rawFormat.includes("dooh")) addCandidate(candidates, "dooh", 84, "existing_format");
  if (rawFormat.includes("youtube")) addCandidate(candidates, "youtube", 82, "existing_format");
  if (rawFormat.includes("cinema") || rawFormat.includes("theater") || rawFormat.includes("theatre")) {
    addCandidate(candidates, "cinema", 88, "existing_format");
  }
  if (rawFormat.includes("email")) addCandidate(candidates, "email", 70, "existing_format");

  const textSignals = [asset?.title, asset?.file_name, asset?.currentVersion?.file_name, asset?.notes]
    .filter(Boolean)
    .join(" ");

  addCandidatesForTextSignals({
    text: textSignals,
    candidates,
  });

  const dimensionToken = sizeToken(dimensions);
  if (dimensionToken && DISPLAY_SIZES.has(dimensionToken)) {
    addCandidate(candidates, "display", 110, "display_size");
    addCandidate(candidates, "mobile", dimensionToken === "320x50" || dimensionToken === "300x50" ? 28 : 0, "mobile_size");
  }

  if (isVideo && ratioLabel === "16:9") {
    addCandidate(candidates, "ctv", 64, "ratio_16_9");
    addCandidate(candidates, "youtube", 62, "ratio_16_9");
    addCandidate(candidates, "video", 54, "ratio_16_9");
    addCandidate(candidates, "dooh", 28, "ratio_16_9");
    addCandidate(candidates, "cinema", 42, "ratio_16_9");
  } else if ((isVideo || isImageAsset(asset)) && (ratioLabel === "9:16" || ratioLabel === "4:5" || ratioLabel === "1:1")) {
    addCandidate(candidates, "instagram", ratioLabel === "9:16" ? 72 : 58, "social_ratio");
    addCandidate(candidates, "facebook", ratioLabel === "1:1" ? 58 : 46, "social_ratio");
    if (isVideo) {
      addCandidate(candidates, "tiktok", ratioLabel === "9:16" ? 78 : 38, "social_ratio");
      addCandidate(
        candidates,
        "youtube",
        ratioLabel === "9:16" ? 70 : ratioLabel === "4:5" ? 60 : 56,
        "social_ratio"
      );
    }
  }

  if (isVideo) {
    addApprovedVideoCompatibilityCandidates({
      candidates,
      approvedPlatformKeys,
      ratioLabel,
      textSignals: normalizeText(textSignals).replace(/[_-]+/g, " "),
    });
  }

  addApprovedLinkedInCompatibilityCandidates({
    candidates,
    approvedPlatformKeys,
    dimensions,
    isVideo,
    isImage: isImageAsset(asset),
  });

  addApprovedLandscapeImageMetaCandidates({
    candidates,
    approvedPlatformKeys,
    dimensions,
    isVideo,
    isImage: isImageAsset(asset),
  });

  if (isLogo) {
    if (approvedPlatformKeys.length === 1) {
      addCandidate(candidates, approvedPlatformKeys[0], 88, "single_approved_brand_asset");
    } else if (approvedPlatformSet.has("display")) {
      addCandidate(candidates, "display", 52, "brand_asset_display");
    }
  }

  const entries = Array.from(candidates.entries()).map(([key, meta]) => ({
    key,
    score: meta.score + (approvedPlatformSet.has(key) ? 18 : 0),
    reasons: meta.reasons,
  }));

  const pool = approvedPlatformKeys.length
    ? entries.filter((entry) => approvedPlatformSet.has(entry.key))
    : entries;

  // Hardened approved gating: when an approved set is present it is
  // AUTHORITATIVE — rank only the approved pool (even when empty) so a
  // platform is never shown unless selected/approved. The unfiltered
  // fallback applies ONLY when there is no approved context at all.
  const rankingSource = approvedPlatformKeys.length ? pool : entries;
  const ranked = rankingSource.sort((left, right) => {
    if (right.score !== left.score) return right.score - left.score;
    return PLATFORM_DEFINITIONS.findIndex((platform) => platform.key === left.key) -
      PLATFORM_DEFINITIONS.findIndex((platform) => platform.key === right.key);
  });

  const best = ranked[0] || null;
  const compatibilityBaseline = best?.score
    ? Math.max(56, best.score - 24)
    : 58;
  const compatibleRanked = ranked.filter((entry) => entry.score >= compatibilityBaseline);

  let platformLabel = "—";
  let platformKey = "";
  let compatiblePlatformKeys = compatibleRanked
    .map((entry) => entry.key)
    .filter(Boolean);

  if (best && best.score >= 58) {
    platformKey = best.key;
    platformLabel = platformLabelFromKey(best.key) || "—";
  } else if (!best && approvedPlatformKeys.length === 1 && (dimensionToken || rawFormat || rawPlatform || isLogo)) {
    platformKey = approvedPlatformKeys[0];
    platformLabel = platformLabelFromKey(platformKey) || "—";
    compatiblePlatformKeys = platformKey ? [platformKey] : [];
  } else if (isLogo && approvedPlatformKeys.length > 1) {
    platformLabel = "Multi-Platform";
  } else if (rawPlatform) {
    platformLabel = humanizeValue(rawPlatform);
  }

  if (!compatiblePlatformKeys.length && platformKey) compatiblePlatformKeys = [platformKey];
  getGuaranteedVideoCompatibilityKeys({ isVideo, ratioLabel }).forEach((key) => {
    if (!compatiblePlatformKeys.includes(key)) compatiblePlatformKeys.push(key);
  });
  compatiblePlatformKeys = Array.from(new Set(compatiblePlatformKeys)).sort(compareCreativePlatformKeys);
  // Draft recognition may identify a selected destination, but never assert
  // technical compatibility while its delivery requirements remain unverified.
  compatiblePlatformKeys = compatiblePlatformKeys.filter(key =>
    !Object.values(SPEC_PLATFORMS[key]?.formats || {}).some(spec => spec.requiresSpecReview)
  );
  const compatiblePlatformLabels = compatiblePlatformKeys
    .map((key) => platformLabelFromKey(key))
    .filter(Boolean);

  const formatLabel = detectFormatLabel({
    asset,
    dimensions,
    ratioLabel,
    isVideo,
    isLogo,
  });

  return {
    platformKey,
    platformLabel,
    compatiblePlatformKeys,
    compatiblePlatformLabels,
    formatLabel,
    ratioLabel: ratioLabel || "—",
    dimensionsLabel: dimensionToken || "—",
    approvedPlatformLabels: approvedPlatformKeys.map((key) => platformLabelFromKey(key)).filter(Boolean),
    isVideo,
    isLogo,
    confidence: best?.score || 0,
  };
};
